import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Key, Trash2 } from 'lucide-react';
import { useTokens, useCreateToken, useRevokeToken } from '@/hooks/useTokens';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export function TokensPage(): JSX.Element {
  const { data } = useTokens();
  const create = useCreateToken();
  const revoke = useRevokeToken();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPlaintext, setNewPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    const result = await create.mutateAsync({ nome: newName });
    setNewPlaintext(result.plaintext);
    setNewName('');
  }

  function handleCopy(): void {
    if (!newPlaintext) return;
    navigator.clipboard.writeText(newPlaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2_000);
  }

  function closeCreateModal(): void {
    setCreateOpen(false);
    setNewPlaintext(null);
    setNewName('');
    setCopied(false);
  }

  async function handleRevoke(id: number, nome: string): Promise<void> {
    if (!confirm(`Revogar token "${nome}"? Qualquer hook usando esse token vai parar de registrar.`)) {
      return;
    }
    await revoke.mutateAsync(id);
  }

  const activeTokens = data?.tokens.filter((t) => !t.revogado_em) ?? [];

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <nav className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Dashboard</Link> ›{' '}
          <Link to="/configuracoes" className="hover:text-foreground">Configurações</Link> › Tokens
        </nav>
        <h1 className="text-2xl font-semibold mt-1">Tokens de API</h1>
        <p className="text-sm text-muted-foreground">
          Tokens usados por hooks externos (Claude Code, CI/CD, agentes) pra registrar itens na
          Central. Um token por máquina recomendado.
        </p>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={() => setCreateOpen(true)}>
            <Key className="h-4 w-4" /> Novo token
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tokens ativos ({activeTokens.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {activeTokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum token ativo. {isAdmin && 'Clique em "Novo token" pra gerar o primeiro.'}
            </p>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Nome</th>
                    <th className="text-left px-3 py-2 font-medium">Escopo</th>
                    <th className="text-left px-3 py-2 font-medium">Último uso</th>
                    <th className="text-left px-3 py-2 font-medium">Último IP</th>
                    <th className="text-right px-3 py-2 font-medium">Criado</th>
                    {isAdmin && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {activeTokens.map((t) => (
                    <tr key={t.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{t.nome}</td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary" className="text-[10px]">
                          {t.escopo}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {t.ultimo_uso_em
                          ? new Date(t.ultimo_uso_em).toLocaleString('pt-BR')
                          : 'nunca usado'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
                        {t.ultimo_ip ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                        {new Date(t.criado_em).toLocaleDateString('pt-BR')}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRevoke(t.id, t.nome)}
                            title="Revogar"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal criar token */}
      <Dialog open={createOpen} onOpenChange={(v) => { if (!v) closeCreateModal(); else setCreateOpen(true); }}>
        <DialogContent>
          {!newPlaintext ? (
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Novo token de API</DialogTitle>
                <DialogDescription>
                  Dê um nome descritivo. Sugerido: o nome da máquina ("MacBook", "VPS").
                </DialogDescription>
              </DialogHeader>
              <div className="my-4">
                <label htmlFor="nome" className="block text-sm font-medium mb-1">
                  Nome
                </label>
                <Input
                  id="nome"
                  required
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="MacBook"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeCreateModal}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={create.isPending || !newName}>
                  {create.isPending ? 'Gerando…' : 'Gerar token'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Token criado ✓</DialogTitle>
                <DialogDescription className="text-destructive">
                  Copie agora. Este valor NÃO será mostrado de novo.
                </DialogDescription>
              </DialogHeader>
              <div className="my-4 space-y-2">
                <div className="bg-muted p-3 rounded-md font-mono text-xs break-all">
                  {newPlaintext}
                </div>
                <Button onClick={handleCopy} variant="outline" className="w-full">
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" /> Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Copiar
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Cole em <code className="bg-muted px-1 rounded">~/.claude/settings.json</code> como{' '}
                  <code className="bg-muted px-1 rounded">CENTRAL_TOKEN</code> (ver instruções em{' '}
                  <Link to="/configuracoes" className="text-primary hover:underline">
                    Configurações
                  </Link>
                  ).
                </p>
              </div>
              <DialogFooter>
                <Button onClick={closeCreateModal}>Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
